// Client-side session identity for the lightweight (SDK-less) analytics beacon.
// PostHog Web Analytics is session-based: every `$pageview` / `$pageleave` needs
// a stable `$session_id` that rotates the way posthog-js rotates it, or the
// sessions table never forms and the dashboard reads ~0.
//
// Session id  → localStorage, shared across tabs, rotates after 30 min of
//               inactivity or 24 h of age (matches posthog-js defaults).
// Window id   → sessionStorage, one per tab for its lifetime.
import { SESSION_IDLE_MS, SESSION_MAX_MS } from './session-lifetime'
import { uuidv7 } from './uuid'

const SESSION_KEY = 'ht_ph_session'
const WINDOW_KEY = 'ht_ph_window'

interface StoredSession {
  id: string
  startTs: number
  lastTs: number
}

// Storage can be disabled in private/embedded browsers. Keep a tab-local
// identity in that case rather than minting a session on every interaction.
let fallbackSession: StoredSession | null = null
let fallbackWindowId: string | null = null

const readSession = (): StoredSession | null => {
  if (fallbackSession) {
    return fallbackSession
  }
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (typeof parsed.id === 'string' && typeof parsed.startTs === 'number' && typeof parsed.lastTs === 'number') {
      return { id: parsed.id, startTs: parsed.startTs, lastTs: parsed.lastTs }
    }
    return null
  } catch {
    return null
  }
}

const writeSession = (session: StoredSession): void => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    fallbackSession = null
  } catch {
    fallbackSession = session
  }
}

const getWindowId = (): string => {
  try {
    const existing = sessionStorage.getItem(WINDOW_KEY)
    if (existing) {
      fallbackWindowId = existing
      return existing
    }
    const fresh = fallbackWindowId ?? uuidv7()
    fallbackWindowId = fresh
    sessionStorage.setItem(WINDOW_KEY, fresh)
    return fresh
  } catch {
    fallbackWindowId ??= uuidv7()
    return fallbackWindowId
  }
}

const isExpired = (session: StoredSession, now: number): boolean =>
  !Number.isFinite(session.startTs) ||
  !Number.isFinite(session.lastTs) ||
  now < session.startTs ||
  now < session.lastTs ||
  now - session.lastTs >= SESSION_IDLE_MS ||
  now - session.startTs >= SESSION_MAX_MS

// Passive events, late unloads and periodic flushes must not touch the idle
// clock or resurrect a session. They may only attach an existing active id.
export const isSessionActive = (sessionId: string): boolean => {
  const stored = readSession()
  return stored !== null && stored.id === sessionId && !isExpired(stored, Date.now())
}

// Returns the active session/window ids, rotating the session if it has gone
// idle or aged out. Call on pageviews and foreground activity, never on passive
// analytics events or pageleave: those must use isSessionActive instead.
export const getSessionContext = (): { sessionId: string; windowId: string } => {
  const now = Date.now()
  const stored = readSession()
  const expired = !stored || isExpired(stored, now)

  const session: StoredSession = expired ? { id: uuidv7(), startTs: now, lastTs: now } : { ...stored, lastTs: now }
  writeSession(session)

  return { sessionId: session.id, windowId: getWindowId() }
}
